import { create } from "zustand";
import { persist } from "zustand/middleware";
import type {
  DiscussionTab,
  ChatMember,
  ChatMessage,
  ChatAttachment,
  Discussion,
  ReadReceipt,
} from "../types/messaging";
import type { User } from "../types/user";
import { messagerieApi } from "../services/api/messagerie";
import { compressImage } from "../utils/compressImage";
import { useAuthStore } from "./auth";
import { useDataStore } from "./user_org";
import { useApiConfigStore } from "./api_config";
import { useInAppNotificationsStore } from "./inAppNotificationsStore";
import { useNotificationPreferencesStore } from "./notificationPreferencesStore";
import firebase from "@services/firebase";
import { collection, query, where, orderBy, limit as fsLimit, onSnapshot, doc, setDoc, deleteDoc, serverTimestamp, updateDoc } from "firebase/firestore";

// --- Helpers ---

const PRESENCE_TIMEOUT_MS = 3 * 60 * 1000; // 3 minutes
const HEARTBEAT_INTERVAL_MS = 60 * 1000; // 60 seconds

/** Check if a user is online based on last_active_time freshness */
function isRecentlyActive(lastActiveTime: any): boolean {
  if (!lastActiveTime) return false;
  try {
    const ts = lastActiveTime.seconds
      ? lastActiveTime.seconds * 1000
      : new Date(lastActiveTime).getTime();
    return Date.now() - ts < PRESENCE_TIMEOUT_MS;
  } catch {
    return false;
  }
}

/** Map a backend User object to a lightweight ChatMember for the UI */
function userToChatMember(user: User): ChatMember {
  return {
    id: user.id,
    name: user.infos?.display_name || user.infos?.full_name || "Inconnu",
    role: user.role || "",
    online: isRecentlyActive(user.activity?.last_active_time),
    photo_url: user.customization?.photo_url || undefined,
    avatar_seed: user.customization?.avatar_seed || undefined,
  };
}

/** Format an ISO timestamp to a readable string like "14:30" or "14:30 Hier" */
function formatTimestamp(iso: string | undefined): string {
  if (!iso) return "";
  try {
    const date = new Date(iso);
    const now = new Date();
    const time = date.toLocaleTimeString("fr-FR", {
      hour: "2-digit",
      minute: "2-digit",
    });
    const isToday =
      date.getDate() === now.getDate() &&
      date.getMonth() === now.getMonth() &&
      date.getFullYear() === now.getFullYear();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const isYesterday =
      date.getDate() === yesterday.getDate() &&
      date.getMonth() === yesterday.getMonth() &&
      date.getFullYear() === yesterday.getFullYear();

    if (isToday) return time;
    if (isYesterday) return `${time} Hier`;
    return `${time} ${date.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" })}`;
  } catch {
    return iso;
  }
}

/** Normalise a backend ChatMessage so legacy camelCase fields are populated */
function normalizeMessage(msg: ChatMessage): ChatMessage {
  const rawTimestamp = msg.created_at || msg.timestamp;
  return {
    ...msg,
    senderId: msg.sender_id || msg.senderId,
    timestamp: formatTimestamp(rawTimestamp),
    replyToId: msg.reply_to_id || msg.replyToId,
    reactions: (msg.reactions || []).map((r) => ({
      ...r,
      userId: r.user_id || r.userId,
    })),
  };
}

/** Normalise a backend Discussion so legacy fields are populated */
function normalizeDiscussion(d: Discussion): Discussion {
  const lm = d.last_message;
  return {
    ...d,
    lastMessage:
      d.lastMessage ??
      (lm
        ? lm.attachment_type
          ? lm.attachment_type === "image"
            ? "Image"
            : lm.attachment_type === "audio"
              ? "Audio"
              : "Fichier"
          : lm.text || ""
        : ""),
    lastMessageTime:
      d.lastMessageTime ?? formatTimestamp(lm?.timestamp),
    unread: false, // unreadCounts store is the sole source of truth for unread state
  };
}

// --- Store ---

interface MessagerieStore {
  // State
  currentUserId: string;
  members: ChatMember[];
  discussions: Discussion[];
  messages: Record<string, ChatMessage[]>;
  activeTab: DiscussionTab;
  selectedDiscussionId: string | null;
  viewingDiscussionId: string | null; // the discussion actually visible (ChatPanel active tab)
  searchQuery: string;
  replyingToMessageId: string | null;
  unreadCounts: Record<string, number>;
  readReceipts: Record<string, ReadReceipt[]>;

  // Loading / connection
  isLoadingDiscussions: boolean;
  isLoadingMoreDiscussions: boolean;
  hasMoreDiscussions: boolean;
  isLoadingMessages: boolean;
  isLoadingOlderMessages: boolean;
  hasMoreMessages: Record<string, boolean>;
  isSending: boolean;
  uploadProgress: Record<string, number>; // optimisticId -> 0-100
  wsConnected: boolean;
  typingUsers: Record<string, string[]>; // discussionId -> userId[]
  _initialized: boolean;
  _unsubDiscussions: (() => void) | null;
  _unsubNotifications: (() => void) | null;
  _unsubPresence: (() => void) | null;
  _heartbeatInterval: ReturnType<typeof setInterval> | null;

  // Actions — async
  initGlobal: () => Promise<void>;
  init: () => Promise<void>;
  fetchDiscussions: (tab?: DiscussionTab) => Promise<void>;
  fetchMoreDiscussions: () => Promise<void>;
  fetchMessages: (discussionId: string) => Promise<void>;
  fetchOlderMessages: (discussionId: string) => Promise<void>;
  fetchReadReceipts: (discussionId: string) => Promise<void>;

  // Actions — sync / triggers
  setActiveTab: (tab: DiscussionTab) => void;
  setSelectedDiscussion: (id: string) => void;
  setSearchQuery: (q: string) => void;
  setViewingDiscussion: (id: string | null) => void;
  setReplyingTo: (messageId: string | null) => void;

  sendMessage: (
    text: string,
    attachment?: ChatAttachment,
    mentions?: string[],
    file?: File,
  ) => Promise<void>;
  sendMessageTo: (
    discussionId: string,
    text: string,
    replyToId?: string | null,
    attachment?: ChatAttachment,
    mentions?: string[],
    file?: File,
    channel?: import("types/messaging").MessageChannel,
  ) => Promise<void>;
  updateMessageIn: (discussionId: string, messageId: string, text: string) => Promise<void>;
  deleteMessageIn: (discussionId: string, messageId: string) => Promise<void>;
  toggleReactionIn: (discussionId: string, messageId: string, emoji: string) => Promise<void>;
  deleteMessage: (messageId: string) => Promise<void>;
  toggleReaction: (messageId: string, emoji: string) => Promise<void>;

  createDiscussion: (
    name: string,
    memberIds: string[],
    tab: DiscussionTab,
  ) => Promise<string | null>;

  followDiscussion: (discussionId: string) => Promise<void>;
  unfollowDiscussion: (discussionId: string) => Promise<void>;

  sendTyping: (discussionId: string) => void;
  clearTyping: (discussionId: string) => void;

  // Derived getters
  getFilteredDiscussions: () => Discussion[];
  getUnreadCount: (tab: DiscussionTab) => number;
  getTotalUnreadCount: () => number;
  getSelectedDiscussion: () => Discussion | undefined;
  getDiscussionMessages: () => ChatMessage[];
  getDiscussionMembers: () => ChatMember[];
  getOnlineCount: () => number;
  getMember: (id: string) => ChatMember | undefined;
  getMessageById: (messageId: string) => ChatMessage | undefined;
  getMatchingMembers: (query: string) => ChatMember[];

}

export const useMessagerieStore = create<MessagerieStore>()(
  persist(
    (set, get) => ({
  currentUserId: "",
  members: [],
  discussions: [],
  messages: {},
  activeTab: "generales",
  selectedDiscussionId: null,
  viewingDiscussionId: null,
  searchQuery: "",
  replyingToMessageId: null,
  unreadCounts: {},
  readReceipts: {},

  isLoadingDiscussions: false,
  isLoadingMoreDiscussions: false,
  hasMoreDiscussions: false,
  isLoadingMessages: false,
  isLoadingOlderMessages: false,
  hasMoreMessages: {},
  isSending: false,
  uploadProgress: {},
  wsConnected: false,
  typingUsers: {},
  _initialized: false,
  _unsubDiscussions: null,
  _unsubNotifications: null,
  _unsubPresence: null,
  _heartbeatInterval: null,

  // ─── Global init (lightweight — called from MainLayout) ─────
  // Called every time orgMembers changes; only does real work once.
  initGlobal: async () => {
    const firebaseUser = useAuthStore.getState().user;
    if (!firebaseUser) return;
    const orgMembers = useDataStore.getState().orgMembers || [];
    if (orgMembers.length === 0) return;
    const orgData = useDataStore.getState().orgData;
    const orgId = orgData?.id;
    if (!orgId) return;

    // Always keep userId + members fresh
    set({ currentUserId: firebaseUser.uid, members: orgMembers.map(userToChatMember) });

    // Only set up listeners once
    if (!get()._initialized) {
      set({ _initialized: true, wsConnected: true });
      try {
        const counts = await messagerieApi.getUnreadCounts();
        set({ unreadCounts: counts });
      } catch (err) {
        console.error("Failed to fetch unread counts:", err);
      }

      // ── Firestore listener: discussions (new messages + discussion changes) ──
      const discQuery = query(
        collection(firebase.db, "discussions"),
        where("org_id", "==", orgId),
      );
      // Track last_message timestamps to detect new messages
      const lastMessageTimes: Record<string, string> = {};
      // Seed with current values so initial snapshot doesn't fire notifications
      get().discussions.forEach((d) => {
        if (d.lastMessageTime) lastMessageTimes[d.id] = d.lastMessageTime;
      });
      let discInitialLoad = true;
      const unsubDisc = onSnapshot(discQuery, (snapshot) => {
        if (discInitialLoad) {
          // Seed timestamps from initial load
          snapshot.docs.forEach((doc) => {
            const data = doc.data();
            const lm = data.last_message;
            if (lm?.timestamp) lastMessageTimes[doc.id] = lm.timestamp;
          });
          discInitialLoad = false;
          // Refresh discussions list
          get().fetchDiscussions(get().activeTab);
          return;
        }

        const state = get();
        const userId = state.currentUserId;
        for (const change of snapshot.docChanges()) {
          if (change.type === "added" || change.type === "modified") {
            const data = change.doc.data();
            const discId = change.doc.id;

            // Skip discussions the user is not a member of
            const members: string[] = data.members || [];
            if (userId && !members.includes(userId)) continue;

            const lm = data.last_message;
            const newTime = lm?.timestamp || "";
            const prevTime = lastMessageTimes[discId] || "";

            // Detect new message (timestamp changed)
            if (newTime && newTime !== prevTime) {
              lastMessageTimes[discId] = newTime;
              // Re-read fresh state for viewingDiscussionId (may have changed since snapshot started)
              const freshState = get();
              const isViewing = freshState.viewingDiscussionId === discId;
              const senderIsMe = lm?.sender_id === freshState.currentUserId;

              if (!isViewing && !senderIsMe) {
                // Increment unread count
                set((s) => ({
                  unreadCounts: {
                    ...s.unreadCounts,
                    [discId]: (s.unreadCounts[discId] || 0) + 1,
                  },
                }));

                // In-app notification is now handled server-side via user_notifications
              }

              // If the user is currently viewing this discussion, refresh messages
              // so the new message appears in the conversation (not just sidebar)
              if (isViewing && !senderIsMe) {
                get().fetchMessages(discId);
              }

              // Update discussion preview in store
              set((s) => ({
                discussions: s.discussions.map((d) =>
                  d.id === discId
                    ? {
                        ...d,
                        lastMessage: lm?.text?.substring(0, 40) || (lm?.attachment_type === "image" ? "Image" : lm?.attachment_type === "audio" ? "Audio" : "Fichier"),
                        lastMessageTime: formatTimestamp(newTime),
                        unread: freshState.viewingDiscussionId !== discId,
                        ...(lm?.sender_id?.startsWith("wa_") ? { whatsapp_last_inbound_at: newTime } : {}),
                      }
                    : d,
                ),
              }));
            }

            // Handle new discussion the user is a member of (not in our list)
            if (change.type === "added" && !state.discussions.some((d) => d.id === discId)) {
              get().fetchDiscussions(get().activeTab);
            }
          }
        }
      }, (err) => {
        console.warn("Discussions Firestore listener error:", err);
      });
      set({ _unsubDiscussions: unsubDisc });

      // ── Re-sync unread counts when returning from background ──
      const handleVisibilityChange = () => {
        if (document.visibilityState === "visible") {
          messagerieApi.getUnreadCounts().then((counts) => {
            // Merge: take the max of listener count vs server count per discussion
            const current = get().unreadCounts;
            const merged: Record<string, number> = { ...current };
            for (const [id, count] of Object.entries(counts)) {
              merged[id] = Math.max(merged[id] || 0, count);
            }
            set({ unreadCounts: merged });
          }).catch(() => {});
        }
      };
      document.addEventListener("visibilitychange", handleVisibilityChange);
      // Store cleanup ref (piggyback on existing unsub pattern)
      const origUnsub = unsubDisc;
      set({ _unsubDiscussions: () => { origUnsub(); document.removeEventListener("visibilitychange", handleVisibilityChange); } });

      // ── Initialize per-user notifications (server-side, replaces org_notifications) ──
      useInAppNotificationsStore.getState().init(firebaseUser.uid);

      // ── Load notification preferences from server ──
      useNotificationPreferencesStore.getState().fetchPreferences();

      // ── Firestore listener: user presence (time-based) ──
      // Cache last_active_time per user so the periodic sweep can re-evaluate
      const lastActiveCache: Record<string, any> = {};

      const usersQuery = query(
        collection(firebase.db, "users"),
        where("org_id", "==", orgId),
      );
      const unsubPresence = onSnapshot(usersQuery, (snapshot) => {
        for (const change of snapshot.docChanges()) {
          if (change.type === "modified" || change.type === "added") {
            const data = change.doc.data();
            const userId = change.doc.id;
            const lastActive = data.activity?.last_active_time;
            lastActiveCache[userId] = lastActive;
            const online = isRecentlyActive(lastActive);

            // Update messagerie store members
            set((s) => ({
              members: s.members.map((m) =>
                m.id === userId ? { ...m, online } : m,
              ),
            }));

            // Update dataStore orgMembers so Team screen also reflects changes
            const dataStore = useDataStore.getState();
            const idx = dataStore.orgMembers.findIndex((m) => m.id === userId);
            if (idx !== -1) {
              const updated = [...dataStore.orgMembers];
              updated[idx] = {
                ...updated[idx],
                activity: { ...updated[idx].activity, ...data.activity },
              };
              useDataStore.setState({ orgMembers: updated });
            }
          }
        }
      }, (err) => {
        console.warn("User presence Firestore listener error:", err);
      });

      // Periodic sweep: re-evaluate online status based on elapsed time
      const presenceSweep = setInterval(() => {
        set((s) => ({
          members: s.members.map((m) => {
            if (m.id === firebaseUser.uid) return m; // skip self
            const cached = lastActiveCache[m.id];
            const online = isRecentlyActive(cached);
            return m.online !== online ? { ...m, online } : m;
          }),
        }));
      }, HEARTBEAT_INTERVAL_MS);

      set({ _unsubPresence: unsubPresence });

      // ── Web presence: heartbeat updates last_active_time periodically ──
      const userRef = doc(firebase.db, "users", firebaseUser.uid);
      const sendHeartbeat = () => {
        updateDoc(userRef, {
          "activity.connected": true,
          "activity.last_active_time": new Date().toISOString(),
        }).catch(() => {});
      };
      // Initial heartbeat
      sendHeartbeat();
      // Periodic heartbeat every 60s
      const heartbeatInterval = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS);
      set({ _heartbeatInterval: heartbeatInterval });

      const handleBeforeUnload = () => {
        updateDoc(userRef, {
          "activity.connected": false,
          "activity.last_active_time": new Date(Date.now() - PRESENCE_TIMEOUT_MS).toISOString(),
        }).catch(() => {});
      };
      const handlePresenceVisibility = () => {
        if (document.visibilityState === "visible") {
          sendHeartbeat();
        }
        // When hidden, just stop heartbeating — user will time out naturally
      };
      window.addEventListener("beforeunload", handleBeforeUnload);
      document.addEventListener("visibilitychange", handlePresenceVisibility);

      // Wrap presence unsub to also clean up event listeners + disconnect
      const origPresenceUnsub = unsubPresence;
      set({
        _unsubPresence: () => {
          origPresenceUnsub();
          clearInterval(heartbeatInterval);
          clearInterval(presenceSweep);
          set({ _heartbeatInterval: null });
          window.removeEventListener("beforeunload", handleBeforeUnload);
          document.removeEventListener("visibilitychange", handlePresenceVisibility);
          updateDoc(userRef, {
            "activity.connected": false,
            "activity.last_active_time": new Date(Date.now() - PRESENCE_TIMEOUT_MS).toISOString(),
          }).catch(() => {});
        },
      });
    }
  },

  // ─── Full init (called when entering messagerie screen) ─────
  init: async () => {
    const firebaseUser = useAuthStore.getState().user;
    if (!firebaseUser) return;

    // Refresh userId + members
    const orgMembers = useDataStore.getState().orgMembers || [];
    set({ currentUserId: firebaseUser.uid, members: orgMembers.map(userToChatMember) });

    // Fetch all discussions (so tab unread dots work across tabs)
    await get().fetchDiscussions();
  },

  // ─── Fetch discussions ───────────────────────────────────────
  fetchDiscussions: async (tab?) => {
    set({ isLoadingDiscussions: true, hasMoreDiscussions: false });
    try {
      const PAGE_SIZE = 25;
      const { items: raw, total } = await messagerieApi.listDiscussions(
        tab ? { tab, limit: PAGE_SIZE } : { limit: PAGE_SIZE },
      );
      const { selectedDiscussionId, activeTab } = get();
      const fetched = raw.map((d) => {
        const disc = normalizeDiscussion(d);
        if (disc.id === selectedDiscussionId) {
          disc.unread = false;
          disc.unread_count = 0;
        }
        return disc;
      });

      const targetTab = tab || activeTab;
      if (tab) {
        const existing = get().discussions.filter((d) => d.tab !== targetTab);
        set({ discussions: [...existing, ...fetched], hasMoreDiscussions: raw.length === PAGE_SIZE });
      } else {
        set({ discussions: fetched, hasMoreDiscussions: raw.length === PAGE_SIZE });
      }

      const currentCounts = { ...get().unreadCounts };
      let countsChanged = false;
      for (const d of fetched) {
        if (!(d.id in currentCounts) && d.unread_count && d.unread_count > 0) {
          currentCounts[d.id] = d.unread_count;
          countsChanged = true;
        }
      }
      if (countsChanged) set({ unreadCounts: currentCounts });
    } catch (err) {
      console.error("Failed to fetch discussions:", err);
    } finally {
      set({ isLoadingDiscussions: false });
    }
  },

  fetchMoreDiscussions: async () => {
    const { isLoadingMoreDiscussions, hasMoreDiscussions, activeTab, discussions } = get();
    if (isLoadingMoreDiscussions || !hasMoreDiscussions) return;
    set({ isLoadingMoreDiscussions: true });
    try {
      const PAGE_SIZE = 25;
      const currentTabCount = discussions.filter((d) => d.tab === activeTab && d.type !== "internal").length;
      const { items: raw } = await messagerieApi.listDiscussions({
        tab: activeTab,
        limit: PAGE_SIZE,
        offset: currentTabCount,
      });
      const { selectedDiscussionId } = get();
      const fetched = raw.map((d) => {
        const disc = normalizeDiscussion(d);
        if (disc.id === selectedDiscussionId) {
          disc.unread = false;
          disc.unread_count = 0;
        }
        return disc;
      });

      const currentCounts = { ...get().unreadCounts };
      let countsChanged = false;
      for (const d of fetched) {
        if (!(d.id in currentCounts) && d.unread_count && d.unread_count > 0) {
          currentCounts[d.id] = d.unread_count;
          countsChanged = true;
        }
      }

      set((s) => ({
        discussions: [...s.discussions, ...fetched],
        hasMoreDiscussions: raw.length === PAGE_SIZE,
        ...(countsChanged ? { unreadCounts: currentCounts } : {}),
      }));
    } catch (err) {
      console.error("Failed to fetch more discussions:", err);
    } finally {
      set({ isLoadingMoreDiscussions: false });
    }
  },

  // ─── Fetch messages ──────────────────────────────────────────
  fetchMessages: async (discussionId) => {
    set({ isLoadingMessages: true });
    try {
      const raw = await messagerieApi.listMessages(discussionId, {
        limit: 50,
      });
      const messages = raw.map(normalizeMessage);
      const realIds = new Set(messages.map((m) => m.id));

      set((s) => {
        // Preserve optimistic messages that haven't been confirmed yet
        const optimistic = (s.messages[discussionId] || []).filter(
          (m) => m.id.startsWith("opt_") && !realIds.has(m.id),
        );
        return {
          messages: { ...s.messages, [discussionId]: [...messages, ...optimistic] },
          hasMoreMessages: { ...s.hasMoreMessages, [discussionId]: raw.length === 50 },
        };
      });

      // Mark as read (backend + local)
      if (messages.length > 0) {
        const lastMsg = messages[messages.length - 1];
        messagerieApi.markAsRead(discussionId, lastMsg.id).catch(() => {});
        set((s) => ({
          unreadCounts: { ...s.unreadCounts, [discussionId]: 0 },
          discussions: s.discussions.map((d) =>
            d.id === discussionId ? { ...d, unread: false, unread_count: 0 } : d,
          ),
        }));
      }

      // Fetch read receipts for this discussion
      get().fetchReadReceipts(discussionId);

    } catch (err) {
      console.error("Failed to fetch messages:", err);
    } finally {
      set({ isLoadingMessages: false });
    }
  },

  // ─── Fetch older messages (scroll up pagination) ──────────
  fetchOlderMessages: async (discussionId) => {
    const { isLoadingOlderMessages, hasMoreMessages, messages } = get();
    if (isLoadingOlderMessages || !hasMoreMessages[discussionId]) return;

    const existing = messages[discussionId] || [];
    if (existing.length === 0) return;

    // Use the oldest message id as cursor
    const oldestMsg = existing[0];
    const beforeId = oldestMsg.id;

    set({ isLoadingOlderMessages: true });
    try {
      const raw = await messagerieApi.listMessages(discussionId, {
        limit: 50,
        before: beforeId,
      });
      const older = raw.map(normalizeMessage);

      set((s) => ({
        messages: {
          ...s.messages,
          [discussionId]: [...older, ...(s.messages[discussionId] || [])],
        },
        hasMoreMessages: { ...s.hasMoreMessages, [discussionId]: raw.length === 50 },
      }));
    } catch (err) {
      console.error("Failed to fetch older messages:", err);
    } finally {
      set({ isLoadingOlderMessages: false });
    }
  },

  // ─── Read receipts ───────────────────────────────────────────
  fetchReadReceipts: async (discussionId) => {
    try {
      const receipts = await messagerieApi.getReadReceipts(discussionId);
      set((s) => ({ readReceipts: { ...s.readReceipts, [discussionId]: receipts } }));
    } catch {
      // Non-critical — silently ignore
    }
  },

  // ─── Tab / selection ─────────────────────────────────────────
  setActiveTab: (tab) => {
    set({ activeTab: tab });
    get().fetchDiscussions(tab);
  },

  setSelectedDiscussion: (id) => {
    // Only clear unreads for the clicked discussion (client side).
    // Internal sibling unreads are cleared when the user actually switches
    // to the Interne tab (via setViewingDiscussion + fetchMessages).
    const discussions = get().discussions.map((d) =>
      d.id === id ? { ...d, unread: false, unread_count: 0 } : d,
    );
    set({
      selectedDiscussionId: id,
      discussions,
      replyingToMessageId: null,
      unreadCounts: { ...get().unreadCounts, [id]: 0 },
    });

    // Fetch messages if not loaded or refresh
    get().fetchMessages(id);
  },

  setSearchQuery: (q) => set({ searchQuery: q }),
  setViewingDiscussion: (id) => set({ viewingDiscussionId: id }),

  setReplyingTo: (messageId) => set({ replyingToMessageId: messageId }),

  // ─── Send message ────────────────────────────────────────────
  sendMessage: async (text, attachment, mentions, file) => {
    const { selectedDiscussionId, currentUserId, replyingToMessageId } = get();
    if (!selectedDiscussionId) return;
    if (!text.trim() && !attachment && !file) return;

    // Compress image before upload
    if (file && file.type.startsWith("image/")) {
      file = await compressImage(file);
    }

    set({ isSending: true });

    const now = new Date();
    const timestamp = now.toLocaleTimeString("fr-FR", {
      hour: "2-digit",
      minute: "2-digit",
    });

    // Optimistic message with blob URL for instant image preview
    const optimisticId = "opt_" + Date.now();
    let optimisticAttachment: ChatAttachment | undefined = attachment
      ? { ...attachment, url: file && file.type.startsWith("image/") ? URL.createObjectURL(file) : attachment.url }
      : undefined;
    // When only a file is provided (no attachment object), create an optimistic attachment
    if (!optimisticAttachment && file) {
      optimisticAttachment = {
        type: file.type.startsWith("image/") ? "image" : "file",
        name: file.name,
        mime_type: file.type,
        size: file.size,
        url: file.type.startsWith("image/") ? URL.createObjectURL(file) : undefined,
      };
    }
    const optimisticMsg: ChatMessage = {
      id: optimisticId,
      senderId: currentUserId,
      sender_id: currentUserId,
      text: text.trim() || undefined,
      attachment: optimisticAttachment,
      reactions: [],
      timestamp,
      replyToId: replyingToMessageId || undefined,
      reply_to_id: replyingToMessageId || undefined,
      mentions: mentions && mentions.length > 0 ? mentions : undefined,
    };

    // Optimistic insert (+ set uploadProgress for file uploads)
    set((s) => ({
      messages: {
        ...s.messages,
        [selectedDiscussionId]: [
          ...(s.messages[selectedDiscussionId] || []),
          optimisticMsg,
        ],
      },
      replyingToMessageId: null,
      uploadProgress: file ? { ...s.uploadProgress, [optimisticId]: 0 } : s.uploadProgress,
    }));

    // Update discussion preview optimistically
    const isAudio = attachment && (attachment.mime_type || attachment.mimeType || "").startsWith("audio/");
    const preview = attachment
      ? attachment.type === "image"
        ? "Image"
        : isAudio
          ? "Audio"
          : `${attachment.name || "Fichier"}`
      : text.trim().substring(0, 40) + (text.trim().length > 40 ? ".." : "");

    set((s) => ({
      discussions: s.discussions.map((d) =>
        d.id === selectedDiscussionId
          ? { ...d, lastMessage: preview, lastMessageTime: timestamp }
          : d,
      ),
    }));

    try {
      // Upload file if provided
      let finalAttachment = attachment;
      if (file) {
        const uploaded = await messagerieApi.uploadAttachment(
          selectedDiscussionId,
          file,
          (progress) => set((s) => ({ uploadProgress: { ...s.uploadProgress, [optimisticId]: progress } })),
        );
        finalAttachment = {
          type: file.type.startsWith("image/") ? "image" : "file",
          name: uploaded.name,
          gcs_path: uploaded.gcs_path,
          mime_type: uploaded.mime_type,
          size: uploaded.size,
        };
      }

      const payload: {
        text?: string;
        attachment?: ChatAttachment;
        reply_to_id?: string;
        mentions?: string[];
      } = {};
      if (text.trim()) payload.text = text.trim();
      if (finalAttachment) payload.attachment = finalAttachment;
      if (replyingToMessageId) payload.reply_to_id = replyingToMessageId;
      if (mentions && mentions.length > 0) payload.mentions = mentions;

      const sent = await messagerieApi.sendMessage(
        selectedDiscussionId,
        payload,
      );

      // Replace optimistic message with real one, then deduplicate
      // (WS may have already appended the real message before the API response)
      const real = normalizeMessage(sent);
      set((s) => {
        const { [optimisticId]: _, ...restProgress } = s.uploadProgress;
        return {
          messages: {
            ...s.messages,
            [selectedDiscussionId]: (s.messages[selectedDiscussionId] || [])
              .map((m) => (m.id === optimisticId ? real : m))
              .filter((m, idx, arr) => idx === arr.findIndex((x) => x.id === m.id)),
          },
          uploadProgress: restProgress,
        };
      });
    } catch (err) {
      console.error("Failed to send message:", err);
      // Remove optimistic message on failure
      set((s) => {
        const { [optimisticId]: _, ...restProgress } = s.uploadProgress;
        return {
          messages: {
            ...s.messages,
            [selectedDiscussionId]: (
              s.messages[selectedDiscussionId] || []
            ).filter((m) => m.id !== optimisticId),
          },
          uploadProgress: restProgress,
        };
      });
    } finally {
      set({ isSending: false });
    }
  },

  // ─── Send message to a specific discussion (without changing global selection) ──
  sendMessageTo: async (discussionId, text, replyToId, attachment, mentions, file, channel) => {
    const { currentUserId } = get();
    if (!discussionId) return;
    if (!text.trim() && !attachment && !file) return;

    // Compress image before upload
    if (file && file.type.startsWith("image/")) {
      file = await compressImage(file);
    }

    set({ isSending: true });

    const now = new Date();
    const timestamp = now.toLocaleTimeString("fr-FR", {
      hour: "2-digit",
      minute: "2-digit",
    });

    const optimisticId = "opt_" + Date.now();
    let optimisticAttachment: ChatAttachment | undefined = attachment
      ? { ...attachment, url: file && file.type.startsWith("image/") ? URL.createObjectURL(file) : attachment.url }
      : undefined;
    if (!optimisticAttachment && file) {
      optimisticAttachment = {
        type: file.type.startsWith("image/") ? "image" : "file",
        name: file.name,
        mime_type: file.type,
        size: file.size,
        url: file.type.startsWith("image/") ? URL.createObjectURL(file) : undefined,
      };
    }
    const optimisticMsg: ChatMessage = {
      id: optimisticId,
      senderId: currentUserId,
      sender_id: currentUserId,
      text: text.trim() || undefined,
      attachment: optimisticAttachment,
      reactions: [],
      timestamp,
      replyToId: replyToId || undefined,
      reply_to_id: replyToId || undefined,
      mentions: mentions && mentions.length > 0 ? mentions : undefined,
    };

    set((s) => ({
      messages: {
        ...s.messages,
        [discussionId]: [
          ...(s.messages[discussionId] || []),
          optimisticMsg,
        ],
      },
      uploadProgress: file ? { ...s.uploadProgress, [optimisticId]: 0 } : s.uploadProgress,
    }));

    try {
      let finalAttachment = attachment;
      if (file) {
        const uploaded = await messagerieApi.uploadAttachment(
          discussionId,
          file,
          (progress) => set((s) => ({ uploadProgress: { ...s.uploadProgress, [optimisticId]: progress } })),
        );
        finalAttachment = {
          type: file.type.startsWith("image/") ? "image" : "file",
          name: uploaded.name,
          gcs_path: uploaded.gcs_path,
          mime_type: uploaded.mime_type,
          size: uploaded.size,
        };
      }

      const payload: {
        text?: string;
        attachment?: ChatAttachment;
        reply_to_id?: string;
        mentions?: string[];
        channel?: import("types/messaging").MessageChannel;
      } = {};
      if (text.trim()) payload.text = text.trim();
      if (finalAttachment) payload.attachment = finalAttachment;
      if (replyToId) payload.reply_to_id = replyToId;
      if (mentions && mentions.length > 0) payload.mentions = mentions;
      if (channel) payload.channel = channel;

      const sent = await messagerieApi.sendMessage(discussionId, payload);
      const real = normalizeMessage(sent);
      set((s) => {
        const { [optimisticId]: _, ...restProgress } = s.uploadProgress;
        return {
          messages: {
            ...s.messages,
            [discussionId]: (s.messages[discussionId] || [])
              .map((m) => (m.id === optimisticId ? real : m))
              .filter((m, idx, arr) => idx === arr.findIndex((x) => x.id === m.id)),
          },
          uploadProgress: restProgress,
        };
      });
    } catch (err) {
      console.error("Failed to send message:", err);
      set((s) => {
        const { [optimisticId]: _, ...restProgress } = s.uploadProgress;
        return {
          messages: {
            ...s.messages,
            [discussionId]: (s.messages[discussionId] || []).filter(
              (m) => m.id !== optimisticId,
            ),
          },
          uploadProgress: restProgress,
        };
      });
    } finally {
      set({ isSending: false });
    }
  },

  // ─── Update message text ─────────────────────────────────────
  updateMessageIn: async (discussionId, messageId, text) => {
    // Optimistic update
    set((s) => ({
      messages: {
        ...s.messages,
        [discussionId]: (s.messages[discussionId] || []).map((m) =>
          m.id === messageId ? { ...m, text, edited: true } : m,
        ),
      },
    }));
    try {
      const sent = await messagerieApi.updateMessage(discussionId, messageId, text);
      const real = normalizeMessage(sent);
      set((s) => ({
        messages: {
          ...s.messages,
          [discussionId]: (s.messages[discussionId] || []).map((m) =>
            m.id === messageId ? real : m,
          ),
        },
      }));
    } catch (err) {
      console.error("Failed to update message:", err);
    }
  },

  // ─── Delete message in a specific discussion ─────────────────
  deleteMessageIn: async (discussionId, messageId) => {
    const { currentUserId } = get();
    const msgs = get().messages[discussionId];
    if (!msgs) return;

    const msg = msgs.find((m) => m.id === messageId);
    if (!msg) return;
    const senderId = msg.sender_id || msg.senderId;
    if (senderId !== currentUserId) return;

    set((s) => ({
      messages: {
        ...s.messages,
        [discussionId]: (s.messages[discussionId] || []).map((m) =>
          m.id === messageId
            ? { ...m, deleted: true, text: undefined, attachment: undefined, reactions: [] }
            : m,
        ),
      },
    }));

    try {
      await messagerieApi.deleteMessage(discussionId, messageId);
    } catch (err) {
      console.error("Failed to delete message:", err);
    }
  },

  // ─── Toggle reaction in a specific discussion ────────────────
  toggleReactionIn: async (discussionId, messageId, emoji) => {
    const { currentUserId } = get();
    if (!discussionId) return;

    set((s) => {
      const msgs = s.messages[discussionId] || [];
      return {
        messages: {
          ...s.messages,
          [discussionId]: msgs.map((m) => {
            if (m.id !== messageId) return m;
            const existing = m.reactions.findIndex(
              (r) =>
                r.emoji === emoji &&
                (r.user_id === currentUserId || r.userId === currentUserId),
            );
            const reactions =
              existing >= 0
                ? m.reactions.filter((_, i) => i !== existing)
                : [
                    ...m.reactions,
                    { emoji, userId: currentUserId, user_id: currentUserId },
                  ];
            return { ...m, reactions };
          }),
        },
      };
    });

    try {
      await messagerieApi.toggleReaction(discussionId, messageId, emoji);
    } catch (err) {
      console.error("Failed to toggle reaction:", err);
    }
  },

  // ─── Delete message ──────────────────────────────────────────
  deleteMessage: async (messageId) => {
    const { selectedDiscussionId, currentUserId } = get();
    if (!selectedDiscussionId) return;

    const msgs = get().messages[selectedDiscussionId];
    if (!msgs) return;

    const msg = msgs.find((m) => m.id === messageId);
    if (!msg) return;
    const senderId = msg.sender_id || msg.senderId;
    if (senderId !== currentUserId) return;

    // Optimistic delete
    set((s) => ({
      messages: {
        ...s.messages,
        [selectedDiscussionId]: (s.messages[selectedDiscussionId] || []).map(
          (m) =>
            m.id === messageId
              ? {
                  ...m,
                  deleted: true,
                  text: undefined,
                  attachment: undefined,
                  reactions: [],
                }
              : m,
        ),
      },
    }));

    try {
      await messagerieApi.deleteMessage(selectedDiscussionId, messageId);
    } catch (err) {
      console.error("Failed to delete message:", err);
    }
  },

  // ─── Toggle reaction ─────────────────────────────────────────
  toggleReaction: async (messageId, emoji) => {
    const { selectedDiscussionId, currentUserId } = get();
    if (!selectedDiscussionId) return;

    // Optimistic toggle
    set((s) => {
      const msgs = s.messages[selectedDiscussionId] || [];
      return {
        messages: {
          ...s.messages,
          [selectedDiscussionId]: msgs.map((m) => {
            if (m.id !== messageId) return m;
            const existing = m.reactions.findIndex(
              (r) =>
                r.emoji === emoji &&
                (r.user_id === currentUserId || r.userId === currentUserId),
            );
            const reactions =
              existing >= 0
                ? m.reactions.filter((_, i) => i !== existing)
                : [
                    ...m.reactions,
                    { emoji, userId: currentUserId, user_id: currentUserId },
                  ];
            return { ...m, reactions };
          }),
        },
      };
    });

    try {
      await messagerieApi.toggleReaction(
        selectedDiscussionId,
        messageId,
        emoji,
      );
    } catch (err) {
      console.error("Failed to toggle reaction:", err);
    }
  },

  // ─── Create discussion ───────────────────────────────────────
  createDiscussion: async (name, memberIds, tab) => {
    try {
      const raw = await messagerieApi.createDiscussion({
        tab,
        name,
        member_ids: memberIds,
      });
      const disc = normalizeDiscussion(raw);

      set((s) => ({
        discussions: [disc, ...s.discussions],
        messages: { ...s.messages, [disc.id]: [] },
        selectedDiscussionId: disc.id,
        activeTab: tab,
      }));
      return disc.id;
    } catch (err) {
      console.error("Failed to create discussion:", err);
      return null;
    }
  },

  // ─── Follow / Unfollow ──────────────────────────────────────
  followDiscussion: async (discussionId: string) => {
    await messagerieApi.followDiscussion(discussionId);
    const userId = get().currentUserId;
    set((s) => ({
      discussions: s.discussions.map((d) =>
        d.id === discussionId && !d.members?.includes(userId)
          ? { ...d, members: [...(d.members || []), userId] }
          : d,
      ),
    }));
    // Refetch ordres tab so the discussion appears in the list
    get().fetchDiscussions("ordres").catch(() => {});
  },

  unfollowDiscussion: async (discussionId: string) => {
    await messagerieApi.unfollowDiscussion(discussionId);
    // Remove the discussion from the list and clear its unread count immediately
    set((s) => {
      const { [discussionId]: _, ...restCounts } = s.unreadCounts;
      return {
        discussions: s.discussions.filter((d) => d.id !== discussionId),
        unreadCounts: restCounts,
      };
    });
    // Refetch to sync with backend
    get().fetchDiscussions(get().activeTab).catch(() => {});
  },

  // ─── Typing indicator ───────────────────────────────────────
  sendTyping: (discussionId: string) => {
    const userId = get().currentUserId;
    if (!userId || !discussionId) return;
    const ref = doc(firebase.db, "discussions", discussionId, "typing", userId);
    setDoc(ref, { timestamp: serverTimestamp() }).catch(() => {});
  },

  clearTyping: (discussionId: string) => {
    const userId = get().currentUserId;
    if (!userId || !discussionId) return;
    const ref = doc(firebase.db, "discussions", discussionId, "typing", userId);
    deleteDoc(ref).catch(() => {});
  },

  // ─── Derived getters ────────────────────────────────────────
  getFilteredDiscussions: () => {
    const { discussions, activeTab, searchQuery } = get();
    return discussions.filter((d) => {
      // Hide internal entity discussions from sidebar — accessed via ChatPanel tabs
      if (d.type === "internal") return false;
      if (d.tab !== activeTab) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        if (
          !d.name.toLowerCase().includes(q) &&
          !(d.lastMessage || "").toLowerCase().includes(q)
        )
          return false;
      }
      return true;
    });
  },

  getUnreadCount: (tab) => {
    const discussions = get().discussions;
    const unreadCounts = get().unreadCounts;
    return discussions.filter((d) => {
      if (d.type === "internal") return false;
      if (d.tab !== tab) return false;
      // Use only unreadCounts (live listener) as source of truth
      if ((unreadCounts[d.id] || 0) > 0) return true;
      // Also count if sibling internal discussion has unreads
      const entityId = d.ordre_id || d.dossier_id;
      if (entityId) {
        const entityKey = d.ordre_id ? "ordre_id" : "dossier_id";
        const sibling = discussions.find((s) =>
          s.type === "internal" && s.id !== d.id &&
          (entityKey === "ordre_id" ? s.ordre_id === entityId : s.dossier_id === entityId)
        );
        if (sibling && (unreadCounts[sibling.id] || 0) > 0) return true;
      }
      return false;
    }).length;
  },

  getTotalUnreadCount: () => {
    return Object.values(get().unreadCounts).reduce((sum, c) => sum + c, 0);
  },

  getSelectedDiscussion: () => {
    const { discussions, selectedDiscussionId } = get();
    return discussions.find((d) => d.id === selectedDiscussionId);
  },

  getDiscussionMessages: () => {
    const { messages, selectedDiscussionId } = get();
    if (!selectedDiscussionId) return [];
    return messages[selectedDiscussionId] || [];
  },

  getDiscussionMembers: () => {
    const disc = get().getSelectedDiscussion();
    if (!disc) return [];
    return get().members.filter((m) => disc.members.includes(m.id));
  },

  getOnlineCount: () => {
    const members = get().getDiscussionMembers();
    return members.filter((m) => m.online).length;
  },

  getMember: (id) => {
    return get().members.find((m) => m.id === id);
  },

  getMessageById: (messageId) => {
    const { messages, selectedDiscussionId } = get();
    if (!selectedDiscussionId) return undefined;
    const msgs = messages[selectedDiscussionId];
    return msgs?.find((m) => m.id === messageId);
  },

  getMatchingMembers: (query) => {
    const members = get().getDiscussionMembers();
    const currentUserId = get().currentUserId;
    if (!query) return members.filter((m) => m.id !== currentUserId);
    const q = query.toLowerCase();
    return members.filter(
      (m) => m.id !== currentUserId && m.name.toLowerCase().startsWith(q),
    );
  },
    }),
    {
      name: "messagerie-prefs",
      partialize: (state) => ({
        activeTab: state.activeTab,
      }),
    },
  ),
);
